/**
 * Print view for ambassador marketing materials — /affiliate/print?format=a5|card
 * Uses @page CSS so "Print → Save as PDF" produces a correctly-sized file.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Printer, ArrowLeft } from 'lucide-react';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';

const PURPLE = '#7A0B7A';
const INK = '#0B0B0E';

const PrintStyles = ({ format }) => (
  <style>{`
    @page { size: ${format === 'card' ? 'A4 portrait' : 'A5 portrait'}; margin: ${format === 'card' ? '12mm 15mm' : '0'}; }
    @media print {
      body { background: #fff !important; }
      .no-print { display: none !important; }
      .sheet { box-shadow: none !important; margin: 0 !important; }
    }
  `}</style>
);

const Flyer = ({ affiliate, shareUrl }) => (
  <div className="sheet mx-auto bg-white shadow-xl overflow-hidden flex flex-col" style={{ width: '148mm', height: '210mm', color: INK }} data-testid="print-flyer">
    <div style={{ background: INK, color: '#fff', padding: '14mm 12mm 10mm', borderBottom: `4px solid ${PURPLE}` }}>
      <p style={{ color: '#c084fc', fontSize: '9pt', letterSpacing: '3px', fontWeight: 700 }}>OX RESIN · AMBASSADOR</p>
      <h1 style={{ fontSize: '26pt', fontWeight: 800, lineHeight: 1.1, marginTop: '4mm' }}>Get trade rates on every order.</h1>
      <p style={{ marginTop: '4mm', fontSize: '10.5pt', opacity: 0.8 }}>ProBound resin · ProEpoxy floors · ProDecor walling</p>
    </div>
    <div style={{ padding: '10mm 12mm', flex: 1, display: 'flex', flexDirection: 'column', gap: '6mm' }}>
      <p style={{ fontSize: '11pt', lineHeight: 1.5 }}>
        Sign up to the Ox Resin Installer Portal through <strong>{affiliate.name}</strong>'s link and you'll unlock trade pricing, free training,
        live quoting tools and a materials calculator built for installers.
      </p>
      <ul style={{ fontSize: '10.5pt', lineHeight: 1.6, paddingLeft: '5mm', listStyle: 'disc' }}>
        <li>Trade pricing on ProBound, ProEpoxy and ProDecor</li>
        <li>Live quoting + materials calculators</li>
        <li>Free installer training &amp; Ox Approved programme</li>
        <li>Next-day delivery on stock lines</li>
      </ul>
      <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: '8mm', border: `2px solid ${PURPLE}`, borderRadius: '4mm', padding: '6mm' }}>
        <QRCodeSVG value={shareUrl} size={140} level="M" fgColor={INK} />
        <div style={{ flex: 1 }}>
          <p style={{ color: PURPLE, fontSize: '8.5pt', letterSpacing: '2px', fontWeight: 700 }}>SCAN TO JOIN — IT'S FREE</p>
          <p style={{ fontSize: '11pt', fontWeight: 700, marginTop: '2mm', wordBreak: 'break-all' }}>{shareUrl.replace(/^https?:\/\//, '')}</p>
          <p style={{ fontSize: '9pt', marginTop: '3mm', color: '#555' }}>Your Ambassador: <strong>{affiliate.name}</strong></p>
        </div>
      </div>
    </div>
    <div style={{ background: '#f3f4f6', padding: '4mm 12mm', fontSize: '8pt', color: '#666' }}>Ox Resin Ltd · oxresin.co.uk</div>
  </div>
);

const BusinessCard = ({ affiliate, shareUrl }) => (
  <div className="flex overflow-hidden" style={{ width: '85mm', height: '55mm', border: '1px dashed #ccc', color: INK }}>
    <div style={{ background: INK, color: '#fff', width: '38mm', padding: '4mm 3.5mm', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', borderRight: `2px solid ${PURPLE}` }}>
      <div>
        <p style={{ color: '#c084fc', fontSize: '5.5pt', letterSpacing: '2px', fontWeight: 700 }}>OX RESIN</p>
        <p style={{ fontSize: '10pt', fontWeight: 800, lineHeight: 1.15, marginTop: '2mm' }}>Trade rates on every order.</p>
      </div>
      <div>
        <p style={{ fontSize: '6.5pt', opacity: 0.7 }}>Ambassador</p>
        <p style={{ fontSize: '8pt', fontWeight: 700 }}>{affiliate.name}</p>
      </div>
    </div>
    <div style={{ flex: 1, padding: '4mm 3.5mm', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2mm' }}>
      <QRCodeSVG value={shareUrl} size={96} level="M" fgColor={INK} />
      <p style={{ fontSize: '5.5pt', color: PURPLE, fontWeight: 700, letterSpacing: '1px' }}>SCAN TO JOIN FREE</p>
      <p style={{ fontSize: '6pt', textAlign: 'center', wordBreak: 'break-all', color: '#444' }}>{shareUrl.replace(/^https?:\/\//, '')}</p>
    </div>
  </div>
);

const CardSheet = ({ affiliate, shareUrl }) => (
  <div className="sheet mx-auto bg-white shadow-xl grid grid-cols-2 content-start" style={{ width: '180mm', minHeight: '275mm', gap: '0' }} data-testid="print-card-sheet">
    {Array.from({ length: 10 }).map((_, i) => <BusinessCard key={i} affiliate={affiliate} shareUrl={shareUrl} />)}
  </div>
);

export default function AffiliatePrint() {
  const [params] = useSearchParams();
  const format = params.get('format') === 'card' ? 'card' : 'a5';
  const [affiliate, setAffiliate] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/affiliate/me`, { credentials: 'include' })
      .then(async (r) => { if (!r.ok) throw new Error('Could not load your Ambassador profile'); setAffiliate(await r.json()); })
      .catch((e) => setError(e.message));
  }, []);

  if (error) return <div className="min-h-screen flex items-center justify-center text-rose-600">{error}</div>;
  if (!affiliate) return <div className="min-h-screen flex items-center justify-center text-gray-500">Preparing your {format === 'card' ? 'business cards' : 'flyer'}…</div>;

  const shareUrl = `${window.location.origin}/join/${affiliate.slug}`;

  return (
    <div className="min-h-screen bg-gray-200 py-6 px-3">
      <PrintStyles format={format} />
      <div className="no-print max-w-[210mm] mx-auto mb-4 flex items-center justify-between gap-3">
        <Button variant="outline" onClick={() => window.close()} data-testid="print-back"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <p className="text-sm text-gray-600 text-center">
          {format === 'card' ? 'Business cards · 10 per A4 sheet — cut along the dashed lines.' : 'A5 flyer — choose "Save as PDF" in the print dialog to download.'}
        </p>
        <Button onClick={() => window.print()} className="bg-[#7A0B7A] hover:bg-[#5e0860] text-white" data-testid="print-now">
          <Printer className="w-4 h-4 mr-1" /> Print / Save PDF
        </Button>
      </div>
      {format === 'card' ? <CardSheet affiliate={affiliate} shareUrl={shareUrl} /> : <Flyer affiliate={affiliate} shareUrl={shareUrl} />}
    </div>
  );
}
